import { memo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import cn from "classnames";
import { Avatar, Box, IconButton, Tooltip } from "@mui/material";
import useStyles from "../Chatbot.styles";
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";
import { useIntl } from "react-intl";

export interface ChatMessageProps {
    id: number | string;
    type: "user" | "assistant";
    content: string;
}

const ChatMessage = memo(({ id, type, content }: ChatMessageProps) => {
    const classes = useStyles();
    const intl = useIntl();
    const copyToastTimeoutRef = useRef<number>();
    const [isCopyToastOpen, setIsCopyToastOpen] = useState(false);

    const isUser = type === "user";

    const handleCopyToClipboard = async () => {
        await navigator.clipboard.writeText(content);
        setIsCopyToastOpen(true);

        if (copyToastTimeoutRef.current) {
            window.clearTimeout(copyToastTimeoutRef.current);
        }

        copyToastTimeoutRef.current = window.setTimeout(() => {
            setIsCopyToastOpen(false);
        }, 1500);
    };

    return (
        <Box display="flex" justifyContent={isUser ? "flex-end" : "flex-start"} mb={2}>
            <Avatar
                variant="circular"
                className={isUser ? classes.avatarUser : classes.avatarContainerSmall}
                sx={{ width: 31, height: 31, order: isUser ? 1 : 0 }}
                aria-hidden
            >
                {isUser ? "You" : <CustomIcon icon={icons.eddyIcon} sx={{ width: 18, height: 18 }} />}
            </Avatar>

            <Box
                className={cn(classes.messageWrapper, {
                    [classes.assistantMessage]: type === "assistant",
                    [classes.userMessage]: isUser,
                })}
            >
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        strong: ({ children }) => {
                            const text = String(children);
                            const stringToReplace = "{highlightedBold}";

                            if (text.startsWith(stringToReplace)) {
                                return (
                                    <strong className={classes.highlightedBold}>
                                        {text.replace(stringToReplace, "")}
                                    </strong>
                                );
                            }

                            return <strong>{children}</strong>;
                        },
                    }}
                >
                    {content}
                </ReactMarkdown>
                {!isUser && (
                    <Box display="flex" justifyContent="flex-end">
                        <Tooltip
                            arrow
                            open={isCopyToastOpen}
                            placement="bottom-end"
                            title={intl.formatMessage({ id: "chatbot.tooltip.copiedToClipboard" })}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                        >
                            <IconButton
                                onClick={handleCopyToClipboard}
                                sx={{ width: 14, height: 14 }}
                                aria-label={intl.formatMessage({ id: "chatbot.ariaLabel.copiedToClipboard" })}
                            >
                                <CustomIcon icon={icons.duplicate} sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

ChatMessage.displayName = "ChatMessage";

export default ChatMessage;
